import React, { Fragment, memo, useMemo } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { IntlProvider } from 'react-intl';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { StaticRouter } from 'react-router-dom/server';
import PropTypes from 'prop-types';

import App from '@/components/App';
import { LOCALES } from '@/constants';
import { GlobalStyle } from '@/styleUtils';
import translationsRU from '@/translations/ru.json';
import translationsUK from '@/translations/uk.json';

const SUPPORTED_LOCALES = Object.values(LOCALES);

/* eslint-disable */
if (!Intl.PluralRules) {
  import('@formatjs/intl-pluralrules/polyfill');
  import('@formatjs/intl-pluralrules/locale-data/uk');
  import('@formatjs/intl-pluralrules/locale-data/ru');
}

if (!Intl.RelativeTimeFormat) {
  import('@formatjs/intl-relativetimeformat/polyfill');
  import('@formatjs/intl-relativetimeformat/locale-data/uk');
  import('@formatjs/intl-relativetimeformat/locale-data/ru');
}
/* eslint-enable */

const translations = {
  uk: translationsUK,
  ru: translationsRU,
};

function Root({
  store,
  locale,
  routerBasename,
  env,
  routerContext = {},
  routerLocation = null,
  helmetContext = {},
}) {
  const messages = useMemo(() => ({ ...translations[locale] }), [locale]);
  const span = (chunks) => <span>{chunks}</span>;
  const br = () => <br />;
  const b = (chunks) => <b>{chunks}</b>;
  const p = (chunks) => <p>{chunks}</p>;
  const ul = (chunks) => <ul>{chunks}</ul>;
  const ol = (chunks) => <ol>{chunks}</ol>;
  const li = (chunks) => <li>{chunks}</li>;

  return (
    <>
      <GlobalStyle />

      <ReduxProvider store={store}>
        <IntlProvider
          locale={locale}
          defaultLocale={locale}
          messages={messages}
          textComponent={Fragment}
          defaultRichTextElements={{
            span,
            br,
            b,
            p,
            ul,
            li,
            ol,
          }}
        >
          {env === 'server' ? (
            <HelmetProvider context={helmetContext}>
              <StaticRouter location={routerLocation} basename={routerBasename}>
                <App routerContext={routerContext} />
              </StaticRouter>
            </HelmetProvider>
          ) : (
            <HelmetProvider>
              <BrowserRouter basename={routerBasename}>
                <App routerContext={routerContext} />
              </BrowserRouter>
            </HelmetProvider>
          )}
        </IntlProvider>
      </ReduxProvider>
    </>
  );
}

Root.propTypes = {
  locale: PropTypes.oneOf(SUPPORTED_LOCALES).isRequired,
  store: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.any])).isRequired,
  routerBasename: PropTypes.string.isRequired,
  routerContext({ env, ...props }, propName) {
    const { [propName]: propValue } = props;

    if (
      env === 'server' &&
      (propValue === undefined || typeof propValue !== 'object')
    ) {
      return new Error('Please provide routerContext!');
    }

    return null;
  },
  routerLocation({ env, ...props }, propName) {
    const { [propName]: propValue } = props;

    if (
      env === 'server' &&
      (propValue === undefined || typeof propValue !== 'string')
    ) {
      return new Error('Please provide routerLocation!');
    }

    return null;
  },
  env: PropTypes.oneOf(['client', 'server']).isRequired,
  helmetContext({ env, ...props }, propName) {
    const { [propName]: propValue } = props;

    if (
      env === 'server' &&
      (propValue === undefined || typeof propValue !== 'string')
    ) {
      return new Error('Please provide helmetContext!');
    }

    return null;
  },
};

export default memo(Root);
